import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, Building, Calendar, Edit, Trash2, Save, Plus, Loader2 } from 'lucide-react';
import { BaseModal, ModalBody, ModalSection } from './index';
import type { Contact, CreateContactData, UpdateContactData } from '../../../shared/types/entities';
import { formatDate } from '../../../shared/utils';
import { toast } from 'sonner';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  contact: Contact | null;
  mode?: 'view' | 'edit' | 'create';
  onUpdate: (id: string, updates: UpdateContactData) => Promise<Contact>;
  onDelete: (id: string) => Promise<void>;
  onCreate?: (data: CreateContactData) => Promise<Contact>;
  onCreated?: (contact: Contact) => void;
  initialCreateData?: Partial<CreateContactData>;
}

export const ContactModal: React.FC<ContactModalProps> = ({
  isOpen,
  onClose,
  contact,
  mode = 'view',
  onUpdate,
  onDelete,
  onCreate,
  onCreated,
  initialCreateData
}) => {
  const [currentMode, setCurrentMode] = useState<'view' | 'edit' | 'create'>(mode);
  const [form, setForm] = useState<UpdateContactData | CreateContactData>({
    name: '',
    email: '',
    phone: '',
    company_name: '',
    notes: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // Initialize form data
  useEffect(() => {
    if (contact) {
      setForm({
        name: contact.name || '',
        email: contact.email || '',
        phone: contact.phone || '',
        company_name: contact.company_name || '',
        notes: contact.notes || ''
      });
      setCurrentMode(mode);
    } else if (mode === 'create') {
      setForm({
        name: initialCreateData?.name || '',
        email: initialCreateData?.email || '',
        phone: initialCreateData?.phone || '',
        company_name: initialCreateData?.company_name || '',
        notes: initialCreateData?.notes || ''
      });
      setCurrentMode('create');
    }
  }, [contact, mode, initialCreateData]);

  const handleChange = (field: keyof UpdateContactData, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!contact || currentMode !== 'edit') return;
    
    setIsSaving(true);
    try {
      await onUpdate(contact.id, form as UpdateContactData);
      setCurrentMode('view');
      toast.success('Contact updated');
    } catch (error) {
      toast.error('Failed to update contact');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!onCreate) return;
    
    const payload = {
      ...form,
      name: ((form as any).name || '').trim(),
    } as CreateContactData;

    if (!payload.name) {
      toast.error('Contact name is required');
      return;
    }

    try {
      setIsSaving(true);
      const created = await onCreate(payload);
      onCreated?.(created);
      toast.success('Contact created');
    } catch (error) {
      toast.error('Failed to create contact');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!contact) return;
    try {
      setDeleting(true);
      await onDelete(contact.id);
      onClose();
      toast.success('Contact deleted');
    } catch (error) {
      toast.error('Failed to delete contact');
    } finally {
      setDeleting(false);
      setConfirmingDelete(false);
    }
  };

  const getModalTitle = () => {
    if (currentMode === 'create') return 'Add Contact';
    if (currentMode === 'edit') return `Edit ${contact?.name || 'Contact'}`;
    return contact?.name || 'Contact Details';
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={getModalTitle()}
      size="lg"
    >
      <ModalBody>
        {/* Contact Header */}
        <ModalSection>
          <div className="flex items-center gap-6">
            {/* Avatar */}
            <div className="w-20 h-20 bg-purple-600 rounded-full flex items-center justify-center text-white text-xl font-bold">
              {contact?.name ? getInitials(contact.name) : (form as any).name ? getInitials((form as any).name) : <User className="h-8 w-8" />}
            </div>
            
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {contact?.name || (form as any).name || 'New Contact'}
              </h1>
              
              <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                {contact?.company_name && (
                  <div className="flex items-center gap-1">
                    <Building className="h-4 w-4" />
                    {contact.company_name}
                  </div>
                )}
                {contact?.created_at && (
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    Added {formatDate(contact.created_at)}
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              {currentMode === 'view' && (
                <button
                  onClick={() => setCurrentMode('edit')}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                >
                  <Edit className="h-4 w-4" />
                  Edit
                </button>
              )}
              
              {currentMode === 'edit' && (
                <>
                  <button
                    onClick={() => setCurrentMode('view')}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 disabled:opacity-50"
                  >
                    {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                </>
              )}
              
              {currentMode === 'create' && (
                <button
                  onClick={handleCreate}
                  disabled={isSaving}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 disabled:opacity-50"
                >
                  {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  {isSaving ? 'Creating...' : 'Create Contact'}
                </button>
              )}
              
              {contact && (
                <button
                  onClick={() => setConfirmingDelete(true)}
                  disabled={deleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 disabled:opacity-50"
                >
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              )}
            </div>
          </div>
        </ModalSection>

        {/* Contact Information */}
        <ModalSection title="Contact Information">
          {currentMode === 'view' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                {contact?.email && (
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-gray-400" />
                    <div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Email</div>
                      <a 
                        href={`mailto:${contact.email}`}
                        className="text-purple-600 hover:text-purple-700 dark:text-purple-400"
                      >
                        {contact.email}
                      </a>
                    </div>
                  </div>
                )}
                
                {contact?.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="h-5 w-5 text-gray-400" />
                    <div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Phone</div>
                      <a 
                        href={`tel:${contact.phone}`}
                        className="text-purple-600 hover:text-purple-700 dark:text-purple-400"
                      >
                        {contact.phone}
                      </a>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="space-y-4">
                {contact?.company_name && (
                  <div className="flex items-center gap-3">
                    <Building className="h-5 w-5 text-gray-400" />
                    <div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Company</div>
                      <div className="text-gray-900 dark:text-white">{contact.company_name}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  value={(form as any).name || ''}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Enter contact name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={(form as any).email || ''}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="contact@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  value={(form as any).phone || ''}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Company
                </label>
                <input
                  type="text"
                  value={(form as any).company_name || ''}
                  onChange={(e) => handleChange('company_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Company name"
                />
              </div>


              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notes
                </label>
                <textarea
                  value={(form as any).notes || ''}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Additional notes about this contact..."
                />
              </div>
            </div>
          )}
        </ModalSection>

        {/* Notes Section (View Mode) */}
        {currentMode === 'view' && contact?.notes && (
          <ModalSection title="Notes">
            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                {contact.notes}
              </p>
            </div>
          </ModalSection>
        )}

        {/* Delete Confirmation */}
        {confirmingDelete && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Delete {contact?.name}?
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                This action cannot be undone. This will permanently delete the contact and all associated data.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setConfirmingDelete(false)}
                  disabled={deleting}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </ModalBody>
    </BaseModal>
  );
};